import { useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { saveBinaryWithPicker } from "../flashtools/lib/saveAs";
import { Binary, Upload, Download, FileDigit, Trash2, Terminal } from "lucide-react";

const VIEW_LIMIT_ROWS = 4096; // render cap (bytes = rows*16)

// ---- Intel HEX helpers ----
function toRecord(bytesArr) {
  let sum = 0, s = "";
  for (const b of bytesArr) { sum = (sum + b) & 0xff; s += b.toString(16).padStart(2, "0").toUpperCase(); }
  const cksum = (0x100 - sum) & 0xff;
  return ":" + s + cksum.toString(16).padStart(2, "0").toUpperCase();
}
function bytesToIntelHex(bytes) {
  const lines = [];
  const recLen = 16;
  let upper = -1;
  for (let i = 0; i < bytes.length; i += recLen) {
    const curUpper = (i >>> 16) & 0xffff;
    if (curUpper !== upper) {
      upper = curUpper;
      lines.push(toRecord([0x02, 0x00, 0x00, 0x04, (upper >> 8) & 0xff, upper & 0xff]));
    }
    const len = Math.min(recLen, bytes.length - i);
    const addr = i & 0xffff;
    const rec = [len, (addr >> 8) & 0xff, addr & 0xff, 0x00];
    for (let j = 0; j < len; j++) rec.push(bytes[i + j]);
    lines.push(toRecord(rec));
  }
  lines.push(":00000001FF");
  return lines.join("\r\n") + "\r\n";
}
function intelHexToBytes(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.startsWith(":"));
  if (lines.length === 0) throw new Error("No valid Intel HEX records (lines must start with ':')");
  const map = {};
  let base = 0, maxAddr = -1, dataRecords = 0;
  for (const l of lines) {
    const len = parseInt(l.substr(1, 2), 16);
    const addr = parseInt(l.substr(3, 4), 16);
    const type = parseInt(l.substr(7, 2), 16);
    const dataHex = l.substr(9, len * 2);
    if (type === 0x00) {
      dataRecords++;
      const full = base + addr;
      for (let i = 0; i < len; i++) {
        const byte = parseInt(dataHex.substr(i * 2, 2), 16);
        map[full + i] = byte;
        if (full + i > maxAddr) maxAddr = full + i;
      }
    } else if (type === 0x04) base = parseInt(dataHex, 16) << 16;
    else if (type === 0x02) base = parseInt(dataHex, 16) << 4;
    else if (type === 0x01) break;
  }
  if (dataRecords === 0) throw new Error("No data records found — is this a valid .hex file?");
  const out = new Uint8Array(maxAddr + 1);
  for (const k in map) out[Number(k)] = map[k];
  return out;
}

const MODES = {
  bin2hex: { title: "BIN → HEX", accept: ".bin", inExt: ".bin", outExt: ".hex", from: "Binary (.bin)", to: "Intel HEX (.hex)", icon: FileDigit },
  hex2bin: { title: "HEX → BIN", accept: ".hex", inExt: ".hex", outExt: ".bin", from: "Intel HEX (.hex)", to: "Binary (.bin)", icon: Binary },
};

export default function HexTool({ mode: initialMode = "bin2hex" }) {
  const [mode, setMode] = useState(initialMode);
  const cfg = MODES[mode];
  const inputRef = useRef(null);
  const [bytes, setBytes] = useState(null);
  const [fileName, setFileName] = useState("");
  const [output, setOutput] = useState(""); // for bin2hex: intel hex text; for hex2bin: kept as bytes
  const [outBlob, setOutBlob] = useState(null);
  const [log, setLog] = useState([]);
  const [dragging, setDragging] = useState(false);

  const addLog = (msg, type = "info") => setLog((p) => [...p, { msg, type, t: new Date().toLocaleTimeString() }]);

  const reset = () => {
    setBytes(null); setFileName(""); setOutput(""); setOutBlob(null); setLog([]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const switchMode = (m) => { if (m === mode) return; setMode(m); reset(); };

  const handleFile = async (file) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(cfg.inExt)) {
      toast.error(`Please select a ${cfg.inExt} file`);
      addLog(`Rejected "${file.name}" — expected ${cfg.inExt}`, "error");
      return;
    }
    setFileName(file.name);
    addLog(`Loaded ${file.name} (${file.size.toLocaleString()} bytes)`);
    try {
      if (mode === "bin2hex") {
        const buf = await file.arrayBuffer();
        const b = new Uint8Array(buf);
        setBytes(b);
        const hex = bytesToIntelHex(b);
        setOutput(hex);
        setOutBlob(new Blob([hex], { type: "text/plain" }));
        addLog(`Converted ${b.length.toLocaleString()} bytes → Intel HEX (${hex.split("\r\n").filter(Boolean).length.toLocaleString()} records, CRLF / UPA-Renesas style)`, "ok");
        toast.success("Converted to Intel HEX");
      } else {
        const text = await file.text();
        const b = intelHexToBytes(text);
        setBytes(b);
        setOutput("");
        setOutBlob(new Blob([b], { type: "application/octet-stream" }));
        addLog(`Parsed Intel HEX → ${b.length.toLocaleString()} bytes binary`, "ok");
        toast.success("Converted to BIN");
      }
    } catch (e) {
      addLog(`Error: ${e.message}`, "error");
      toast.error(e.message);
      setBytes(null); setOutBlob(null);
    }
  };

  const download = async () => {
    if (!outBlob) return;
    const base = fileName.replace(/\.[^.]+$/, "");
    const bytes = new Uint8Array(await outBlob.arrayBuffer());
    const ok = await saveBinaryWithPicker(bytes, base + cfg.outExt, { toast });
    if (ok) addLog(`Saved ${base + cfg.outExt}`, "ok");
  };

  const rows = useMemo(() => {
    if (!bytes) return [];
    const total = Math.min(bytes.length, VIEW_LIMIT_ROWS * 16);
    const r = [];
    for (let i = 0; i < total; i += 16) {
      const slice = bytes.subarray(i, Math.min(i + 16, total));
      const hex = Array.from(slice).map((b) => b.toString(16).padStart(2, "0").toUpperCase());
      const ascii = Array.from(slice).map((b) => (b >= 32 && b < 127 ? String.fromCharCode(b) : ".")).join("");
      r.push({ off: i, hex, ascii });
    }
    return r;
  }, [bytes]);

  const truncated = bytes && bytes.length > VIEW_LIMIT_ROWS * 16;
  const Icon = cfg.icon;

  return (
    <div className="p-4 sm:p-8 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400/90 flex items-center gap-2"><Icon className="h-4 w-4" /> Converter</div>
        <h1 className="font-head font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Bin Converter</h1>
        <p className="text-slate-500 text-sm mt-1">Browse a <span className="text-cyan-300 font-mono-tt">{cfg.from}</span> file, inspect it in the hex viewer, then export as <span className="text-cyan-300 font-mono-tt">{cfg.to}</span>.</p>

        <div data-testid="bin-converter-mode-toggle" className="inline-flex mt-4 p-1 rounded-lg bg-[#0c1017] border border-slate-800">
          {Object.entries(MODES).map(([key, m]) => (
            <button
              key={key}
              data-testid={`bin-converter-mode-${key}`}
              onClick={() => switchMode(key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-bold transition-colors ${mode === key ? "bg-gradient-to-r from-cyan-400 to-teal-500 text-slate-950" : "text-slate-400 hover:text-white"}`}
            >
              <m.icon className="h-4 w-4" /> {m.title}
            </button>
          ))}
        </div>
      </div>

      {/* Dropzone */}
      <div
        data-testid="hex-tool-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
        className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors ${dragging ? "border-cyan-400 bg-cyan-400/5" : "border-slate-700 bg-[#0c1017]"}`}
      >
        <input ref={inputRef} data-testid="hex-tool-file-input" type="file" accept={cfg.accept} className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])} />
        <Upload className="h-8 w-8 text-slate-500 mx-auto mb-3" />
        <p className="text-slate-300 font-semibold">Drop your <span className="font-mono-tt text-cyan-300">{cfg.inExt}</span> file here</p>
        <p className="text-slate-600 text-sm mt-1">or</p>
        <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
          <button data-testid="hex-tool-browse-btn" onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold transition-colors">
            <Upload className="h-4 w-4" /> Browse {cfg.inExt}
          </button>
          {bytes && (
            <button data-testid="hex-tool-clear-btn" onClick={reset}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold transition-colors">
              <Trash2 className="h-4 w-4" /> Clear
            </button>
          )}
        </div>
        {fileName && <p className="mt-4 font-mono-tt text-sm text-slate-400" data-testid="hex-tool-filename">Loaded: <span className="text-white">{fileName}</span> {bytes && `· ${bytes.length.toLocaleString()} bytes`}</p>}
      </div>

      {bytes && (
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <button data-testid="hex-tool-download-btn" onClick={download}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold transition-colors">
            <Download className="h-4 w-4" /> Download {cfg.outExt}
          </button>
          <span className="text-xs font-mono-tt text-slate-500">{cfg.from} → {cfg.to}</span>
        </div>
      )}

      {/* Hex viewer */}
      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        <div className="lg:col-span-2 rounded-xl border border-slate-800 bg-[#07090e] overflow-hidden">
          <div className="px-4 py-2.5 bg-[#121824] border-b border-slate-800 flex items-center gap-2">
            <Terminal className="h-4 w-4 text-cyan-400" />
            <span className="font-mono-tt text-xs uppercase tracking-wider text-cyan-300">Hex Viewer</span>
            {truncated && <span className="ml-auto text-[10px] font-mono-tt text-slate-500">showing first {(VIEW_LIMIT_ROWS * 16).toLocaleString()} bytes</span>}
          </div>
          <div data-testid="hex-viewer" className="overflow-auto max-h-[560px] font-mono-tt text-[12px] leading-relaxed p-3">
            {!bytes && <div className="text-slate-600 p-6 text-center">No data loaded. Browse a {cfg.inExt} file to begin.</div>}
            {rows.map((r) => (
              <div key={r.off} className="flex gap-4 whitespace-pre hover:bg-cyan-400/5 px-1">
                <span className="text-cyan-400/70 select-none">{r.off.toString(16).padStart(8, "0").toUpperCase()}</span>
                <span className="text-slate-300 flex-1">
                  {r.hex.map((h, i) => (
                    <span key={i} className={i === 8 ? "ml-2" : ""}>{h} </span>
                  ))}
                </span>
                <span className="text-slate-500">{r.ascii}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Console */}
        <div className="rounded-xl border border-slate-800 bg-[#07090e] overflow-hidden">
          <div className="px-4 py-2.5 bg-[#121824] border-b border-slate-800 flex items-center gap-2">
            <Terminal className="h-4 w-4 text-zinc-400" />
            <span className="font-mono-tt text-xs uppercase tracking-wider text-zinc-300">Console</span>
          </div>
          <div data-testid="hex-console" className="overflow-auto max-h-[560px] font-mono-tt text-[12px] p-3 space-y-1">
            {log.length === 0 && <div className="text-slate-600">// waiting for file…</div>}
            {log.map((l, i) => (
              <div key={i} className={l.type === "error" ? "text-cyan-300" : l.type === "ok" ? "text-emerald-400" : "text-slate-400"}>
                <span className="text-slate-600">[{l.t}]</span> {l.msg}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Intel HEX text output for bin2hex */}
      {mode === "bin2hex" && output && (
        <div className="rounded-xl border border-slate-800 bg-[#07090e] overflow-hidden mt-5">
          <div className="px-4 py-2.5 bg-[#121824] border-b border-slate-800 flex items-center gap-2">
            <span className="font-mono-tt text-xs uppercase tracking-wider text-cyan-300">Intel HEX Output</span>
          </div>
          <pre data-testid="hex-tool-output-text" className="overflow-auto max-h-[300px] font-mono-tt text-[12px] text-slate-300 p-3">{output.split("\r\n").slice(0, 2000).join("\n")}{output.split("\r\n").length > 2000 ? "\n… (truncated preview — full file in download)" : ""}</pre>
        </div>
      )}
    </div>
  );
}
